import type { PermissionKey } from '@nexa/auth';

// The view permission of every console surface (brief §5 C1). ONE table, read by the
// sidebar (which items show) and by the route guards in `routes/index.tsx` (which URLs
// open) — so a nav item and its route can never disagree. Keys are stable nav ids.
//
// Sidebar surfaces, each its module's view key from the catalog (§4):
export const NAV_PERMISSIONS = {
  dashboard: 'dashboard.view',
  disruptions: 'disruptions.view',
  cases: 'cases.view',
  flights: 'flights.view',
  passengers: 'passengers.search',
  reservations: 'reservations.view',
  countersQr: 'counters.qr',
  providerSim: 'provider_sim.answer',
  reports: 'reports.view',
  billing: 'billing.view',
  policies: 'policies.view',
  contracts: 'contracts.view',
  formats: 'formats.view',
  providers: 'providers.view',
  users: 'users.view',
  roles: 'roles.view',
} as const satisfies Record<string, PermissionKey>;

export type NavId = keyof typeof NAV_PERMISSIONS;

/** Where each nav id lands — the sidebar links here and `/` redirects to the first allowed. */
export const NAV_PATHS: Readonly<Record<NavId, string>> = {
  dashboard: '/dashboard',
  disruptions: '/disruptions',
  cases: '/cases',
  flights: '/flights',
  passengers: '/passengers',
  reservations: '/reservations',
  countersQr: '/counters/qr',
  providerSim: '/provider-sim',
  reports: '/reports',
  billing: '/billing',
  policies: '/policies',
  contracts: '/contracts',
  formats: '/formats',
  providers: '/providers',
  users: '/users',
  roles: '/roles',
};

/** Sidebar order (#841) — also the order `/` tries when picking a home. */
export const NAV_ORDER: readonly NavId[] = [
  'dashboard',
  'disruptions',
  'cases',
  'flights',
  'passengers',
  'reservations',
  'countersQr',
  'providerSim',
  'reports',
  'billing',
  'policies',
  'contracts',
  'formats',
  'providers',
  'users',
  'roles',
];

/** Every member may open their own profile (brief §5 P7) — the home of last resort. */
export const FALLBACK_HOME = '/profile';

/** The first surface `can` allows, in sidebar order; the profile when none is. */
export function homePath(can: (permission: PermissionKey) => boolean): string {
  const first = NAV_ORDER.find((id) => can(NAV_PERMISSIONS[id]));
  return first ? NAV_PATHS[first] : FALLBACK_HOME;
}
